import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Gift, ShoppingBag, CircleDollarSign, Home, Gamepad2, Wallet, Headphones, Copy, Check, Phone, TicketCheck, Calendar, CalendarDays } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { InstallAppNudge } from '../../components/InstallPrompt';
import MiniRouletteLogo from './MiniRouletteLogo';

const PHONE_REWARD_CACHE_PREFIX = 'slotsoceans.phone-reward-summary.v1';

const phoneRewardCacheKeyForUser = (user) => {
  const userId = String(user?.id || '').trim();
  const tenantId = String(user?.tenant_id || user?.tenant_slug || 'main').trim();
  return userId ? `${PHONE_REWARD_CACHE_PREFIX}:${tenantId}:${userId}` : '';
};

const normalizePhoneRewardStatus = (status) => {
  if (!status) return null;
  const enabled = status.mobile_verification_reward_enabled
    ?? status.phone_verify_reward_enabled;
  const amount = status.mobile_verification_reward_fp
    ?? status.phone_verify_reward_fp;
  if (typeof enabled !== 'boolean' || !Number.isFinite(Number(amount))) return null;
  return {
    ...status,
    mobile_verification_reward_enabled: enabled,
    mobile_verification_reward_fp: Number(amount),
  };
};

const readCachedPhoneRewardStatus = (user) => {
  const key = phoneRewardCacheKeyForUser(user);
  if (!key || typeof window === 'undefined') return null;
  try {
    return normalizePhoneRewardStatus(JSON.parse(window.sessionStorage.getItem(key) || 'null'));
  } catch {
    return null;
  }
};

const writeCachedPhoneRewardStatus = (key, status) => {
  const normalized = normalizePhoneRewardStatus(status);
  if (!key || !normalized || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify({
      mobile_verification_reward_enabled: normalized.mobile_verification_reward_enabled,
      mobile_verification_reward_fp: normalized.mobile_verification_reward_fp,
      cached_at: Date.now(),
    }));
  } catch {
    // Storage can be disabled in private/restricted browser modes.
  }
};

const initialPhoneRewardStatusForUser = (user) => (
  normalizePhoneRewardStatus(user) || readCachedPhoneRewardStatus(user)
);

/**
 * Explore & Earn — hub page with tile grid linking to sub-pages.
 */
export default function ExploreEarnPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const phoneRewardCacheKey = phoneRewardCacheKeyForUser(user);
  const [phoneVerifyStatus, setPhoneVerifyStatus] = useState(() => initialPhoneRewardStatusForUser(user));
  const phoneVerified = !!(
    user?.phone_verified ||
    user?.phoneVerified ||
    user?.phone_verified_at ||
    user?.mobile_verification_bonus_awarded ||
    phoneVerifyStatus?.verified ||
    phoneVerifyStatus?.phone_verified ||
    phoneVerifyStatus?.phone_verified_at
  );
  const [referralStats, setReferralStats] = useState({ referral_code: '' });
  const [referralRules, setReferralRules] = useState(null);
  const [referralRulesFailed, setReferralRulesFailed] = useState(false);
  const [marketplaceEnabled, setMarketplaceEnabled] = useState(false);
  const [copiedReferralCode, setCopiedReferralCode] = useState(false);
  const [taskAvailability, setTaskAvailability] = useState({
    daily_enabled: true,
    weekly_enabled: true,
    daily_has_earned_rewards: false,
    weekly_has_earned_rewards: false,
  });
  const [branding, setBranding] = useState({
    brand_name: 'Slots Oceans',
    logo_url: '/api/tenants/public/slotsoceans/install-icon',
    home_screen: {},
  });

  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const basePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';
  // The authenticated user and referral stats both come from the users
  // collection. Never manufacture a display code from username/email: that
  // briefly exposed an invalid code before the stats request completed.
  const referralCode = String(user?.referral_code || referralStats.referral_code || '')
    .trim()
    .toUpperCase();
  const referralCodeReady = Boolean(referralCode);
  const referrerBonus = Number(referralRules?.player_referrer_bonus_sc || 0);
  const referredBonus = Number(referralRules?.player_referred_bonus_sc || 0);
  const loadPercent = Number(referralRules?.player_referrer_load_percent || 0);
  const referralEarnText = referralRules
    ? [
        `Earn $${referrerBonus.toLocaleString(undefined, { maximumFractionDigits: 2 })} + ${referredBonus.toLocaleString(undefined, { maximumFractionDigits: 2 })} FP`,
        loadPercent > 0 ? `then up to ${loadPercent.toLocaleString(undefined, { maximumFractionDigits: 2 })}%` : '',
      ].filter(Boolean).join(', ')
    : (referralRulesFailed ? 'Reward details unavailable' : 'Loading reward details...');
  const phoneRewardFp = Number(phoneVerifyStatus?.mobile_verification_reward_fp ?? 0);
  const phoneRewardEnabled = phoneVerifyStatus !== null
    && phoneVerifyStatus?.mobile_verification_reward_enabled !== false
    && phoneRewardFp > 0;
  const phoneRewardLabel = `${phoneRewardFp.toLocaleString(undefined, { maximumFractionDigits: 0 })} FP`;

  useEffect(() => {
    let live = true;
    api.get('/api/referrals/stats')
      .then((res) => {
        if (live && res?.data) setReferralStats((prev) => ({ ...prev, ...res.data }));
      })
      .catch(() => {});
    api.get('/api/referrals/rules')
      .then((res) => {
        if (live && res?.data) {
          setReferralRules(res.data);
          setReferralRulesFailed(false);
        }
      })
      .catch(() => {
        if (live) setReferralRulesFailed(true);
      });
    api.get('/api/marketplace/items')
      .then((res) => {
        if (live) setMarketplaceEnabled(res.data?.enabled !== false);
      })
      .catch(() => {
        if (live) setMarketplaceEnabled(false);
      });
    api.get('/api/branding', {
      params: { _scope: `rewards-branding:${tenantSlug || 'slotsoceans'}`, _ts: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
      .then((res) => {
        if (live && res?.data) setBranding((prev) => ({ ...prev, ...res.data }));
      })
      .catch(() => {});
    return () => { live = false; };
  }, [tenantSlug]);

  useEffect(() => {
    let live = true;
    api.get('/api/tasks/availability')
      .then((res) => {
        if (!live) return;
        const data = res?.data || {};
        setTaskAvailability({
          daily_enabled: data.daily_enabled !== false,
          weekly_enabled: data.weekly_enabled !== false,
          daily_has_earned_rewards: data.daily_has_earned_rewards === true,
          weekly_has_earned_rewards: data.weekly_has_earned_rewards === true,
        });
      })
      .catch(() => {
        // Keep task navigation stable through a transient availability error.
      });
    return () => { live = false; };
  }, [user?.id, user?.tenant_id]);

  useEffect(() => {
    let live = true;
    const initial = initialPhoneRewardStatusForUser(user);
    setPhoneVerifyStatus(initial);
    const loadPhoneVerifyStatus = () => {
      api.get('/api/users/me/phone-verification/status')
        .then((res) => {
          if (!live) return;
          const next = normalizePhoneRewardStatus(res.data) || initial;
          setPhoneVerifyStatus(next);
          if (next) writeCachedPhoneRewardStatus(phoneRewardCacheKey, next);
        })
        .catch(() => {
          // Preserve the auth bootstrap/cache so transient requests cannot
          // remove and reinsert the CTA while the player is looking at it.
          if (live && initial) setPhoneVerifyStatus(initial);
        });
    };
    loadPhoneVerifyStatus();
    window.addEventListener('focus', loadPhoneVerifyStatus);
    window.addEventListener('vl:phone-verified', loadPhoneVerifyStatus);
    return () => {
      live = false;
      window.removeEventListener('focus', loadPhoneVerifyStatus);
      window.removeEventListener('vl:phone-verified', loadPhoneVerifyStatus);
    };
  }, [user?.id, user?.tenant_id, phoneRewardCacheKey]);

  const copyReferralCode = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!referralCodeReady) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopiedReferralCode(true);
      window.setTimeout(() => setCopiedReferralCode(false), 1600);
    } catch {
      setCopiedReferralCode(false);
    }
  };

  const tiles = [
    {
      key: 'referral',
      label: 'Referral & Earn',
      sub: referralEarnText,
      icon: CircleDollarSign,
      iconShape: 'coin',
      gradient: 'linear-gradient(135deg, #004D61 0%, #006B85 100%)',
      iconColor: '#E0F4F7',
      ring: 'rgba(224,244,247,0.24)',
      iconShadow: '0 7px 16px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.18)'
    },
    {
      key: 'promo-code',
      label: 'Promo Code',
      sub: 'Claim your one-time task reward',
      icon: TicketCheck,
      gradient: 'linear-gradient(135deg, #8A5A00 0%, #D4AF37 100%)',
      iconColor: '#FFF7CC',
      ring: 'rgba(255,217,61,0.30)',
      iconShadow: '0 7px 16px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.22)',
    },
    {
      key: 'daily-tasks',
      label: 'Daily Tasks',
      sub: taskAvailability.daily_has_earned_rewards
        ? 'Reward ready to claim'
        : 'Quick wins, every 24 hours',
      icon: Calendar,
      gradient: 'linear-gradient(135deg, #10B981 0%, #047857 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(16,185,129,0.55)',
      hot: taskAvailability.daily_has_earned_rewards,
    },
    {
      key: 'weekly-tasks',
      label: 'Weekly Tasks',
      sub: taskAvailability.weekly_has_earned_rewards
        ? 'Reward ready to claim'
        : 'Bigger goals, bigger rewards',
      icon: CalendarDays,
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #B45309 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(245,158,11,0.55)',
      hot: taskAvailability.weekly_has_earned_rewards,
    },
    {
      key: 'roulette',
      label: 'Roulette',
      sub: 'Spin to win every day',
      logo: 'roulette',
      gradient: 'linear-gradient(135deg, #063946 0%, #0A5B6A 100%)',
      iconColor: '#FFFFFF',
      ring: 'rgba(224,244,247,0.24)',
      iconShadow: '0 7px 16px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
    },
    {
      key: 'marketplace',
      label: 'Marketplace',
      sub: 'Spend coins on rewards',
      icon: ShoppingBag,
      gradient: 'linear-gradient(135deg, #063946 0%, #0A5B6A 100%)',
      iconColor: '#FFE66D',
      ring: 'rgba(224,244,247,0.24)',
      iconShadow: '0 7px 16px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.18)',
    },
  ];
  const visibleTiles = tiles
    .filter((tile) => tile.key !== 'marketplace' || marketplaceEnabled)
    .filter((tile) => !(
      tile.key === 'daily-tasks'
      && !taskAvailability.daily_enabled
      && !taskAvailability.daily_has_earned_rewards
    ))
    .filter((tile) => !(
      tile.key === 'weekly-tasks'
      && !taskAvailability.weekly_enabled
      && !taskAvailability.weekly_has_earned_rewards
    ));

  return (
    <div
      className="player-app player-desktop-side-nav-host min-h-screen pb-32"
      style={{ color: '#FFFFFF' }}
      data-testid="explore-earn-page"
    >
      {/* Header — scrolls with content (matches home behavior) */}
      <div
        className="px-4 sm:px-6 py-4 flex items-center gap-3"
        style={{
          background: '#000000',
        }}
      >
        <button
          onClick={() => navigate(homePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid="explore-earn-back"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1
            className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2"
            style={{ color: '#FFFFFF' }}
          >
            <Gift className="w-6 h-6" style={{ color: '#FFD93D' }} />
            Explore &amp; Earn
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Complete tasks. Unlock rewards.
          </p>
        </div>
      </div>

      {/* Tile grid */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {!phoneVerified && phoneRewardEnabled && (
          <button
            type="button"
            onClick={() => navigate(`${homePath}?open=phone-verify`)}
            className="mb-4 flex w-full items-center gap-3 rounded-[20px] border p-3 text-left transition active:scale-[0.99]"
            style={{
              background: 'linear-gradient(135deg, rgba(76,28,88,0.82), rgba(35,55,92,0.78))',
              borderColor: 'rgba(224,244,247,0.18)',
              boxShadow: '0 14px 28px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)',
            }}
            data-testid="rewards-phone-verify-bonus-card"
          >
            <span
              className="grid h-12 w-12 shrink-0 place-items-center rounded-[16px]"
              style={{
                background: 'linear-gradient(135deg, #40D8FF, #2E63FF)',
                boxShadow: '0 10px 22px rgba(46,99,255,0.28)',
              }}
            >
              <Phone className="h-7 w-7 text-white" strokeWidth={2.7} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[15px] font-black leading-tight text-white sm:text-base">Verify your number</span>
              <span className="mt-0.5 block text-[12px] font-bold text-white/72 sm:text-[13px]">Get {phoneRewardLabel} after phone verification.</span>
            </span>
            <span
              className="shrink-0 rounded-xl px-3 py-2 text-[13px] font-black"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.96), rgba(224,244,247,0.88))',
                color: '#004D61',
                boxShadow: '0 8px 18px rgba(0,0,0,0.22)',
              }}
            >
              +{phoneRewardLabel}
            </span>
          </button>
        )}

        {branding.home_screen?.install_enabled !== false && (
          <InstallAppNudge
            className="mb-4"
            brandLogo={branding.home_screen?.icon_url || branding.logo_url || '/api/tenants/public/slotsoceans/install-icon'}
            brandName={branding.home_screen?.app_name || branding.brand_name || 'Slots Oceans'}
            title={branding.home_screen?.install_title}
            bio={branding.home_screen?.install_bio}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {visibleTiles.map((t) => {
            const Icon = t.icon;
            const isHot = Boolean(t.hot);
            const isReferral = t.key === 'referral';
            const tilePath = `${basePath}/${t.key}`;
            const openTile = () => navigate(tilePath);
            const onTileKeyDown = (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openTile();
              }
            };
            return (
              <div
                key={t.key}
                role="button"
                tabIndex={0}
                onClick={openTile}
                onKeyDown={onTileKeyDown}
                className={`cursor-pointer text-left rounded-2xl p-5 transition active:scale-[0.99] hover:brightness-110 flex items-center gap-4 relative overflow-hidden ${isReferral ? 'md:col-span-3' : ''}`}
                style={{
                  background: isHot
                    ? 'linear-gradient(135deg, rgba(255,107,0,0.18) 0%, rgba(255,26,26,0.10) 100%)'
                    : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${t.ring}`,
                  boxShadow: isHot
                    ? '0 0 0 1px rgba(255,107,0,0.45), 0 14px 36px rgba(255,107,0,0.28), inset 0 1px 0 rgba(255,255,255,0.10)'
                    : `0 8px 22px ${t.ring.replace('0.55', '0.18')}, inset 0 1px 0 rgba(255,255,255,0.05)`,
                }}
                data-testid={`explore-tile-${t.key}`}
              >
                {t.logo === 'roulette' ? (
                  <span
                    className="relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-[18px]"
                    style={{
                      background: t.gradient,
                      boxShadow: t.iconShadow || `0 8px 18px ${t.ring}, inset 0 1px 0 rgba(255,255,255,0.45)`,
                    }}
                    aria-hidden
                  >
                    <span className="absolute inset-[5px] rounded-[14px] bg-black/55 shadow-inner" />
                    <span className="absolute inset-0 rounded-[18px] ring-1 ring-white/30" />
                    <span className="relative grid h-11 w-11 place-items-center rounded-full bg-white/10">
                      <MiniRouletteLogo size={38} spin={false} />
                    </span>
                  </span>
                ) : t.iconShape === 'coin' ? (
                  <span
                    className="relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-full"
                    style={{
                      background: t.gradient,
                      border: '1px solid rgba(224,244,247,0.42)',
                      boxShadow: t.iconShadow || `0 8px 18px ${t.ring}, inset 0 1px 0 rgba(255,255,255,0.45)`,
                    }}
                    aria-hidden
                  >
                    <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_22%,rgba(255,255,255,0.36),transparent_38%)]" />
                    <span className="absolute inset-[7px] rounded-full border border-white/20 bg-black/12" />
                    <Icon className="relative h-8 w-8 drop-shadow-[0_2px_8px_rgba(0,0,0,0.36)]" style={{ color: t.iconColor }} strokeWidth={2.45} />
                  </span>
                ) : (
                  <span
                    className="relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-[18px]"
                    style={{
                      background: t.gradient,
                      boxShadow: isHot
                        ? '0 12px 30px rgba(255,107,0,0.50), 0 0 28px rgba(255,217,61,0.34), inset 0 1px 0 rgba(255,255,255,0.55)'
                        : (t.iconShadow || `0 8px 18px ${t.ring}, inset 0 1px 0 rgba(255,255,255,0.45)`),
                    }}
                    aria-hidden
                  >
                    <span className="absolute inset-0 bg-[radial-gradient(circle_at_28%_18%,rgba(255,255,255,0.55),transparent_34%)]" />
                    <span className="absolute inset-[4px] rounded-[15px] border border-white/28" />
                    <Icon className="relative h-7 w-7 drop-shadow-[0_2px_8px_rgba(0,0,0,0.42)]" style={{ color: t.iconColor }} strokeWidth={2.65} />
                  </span>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p
                        className="display font-extrabold text-base sm:text-lg leading-tight"
                        style={{ color: '#FFFFFF' }}
                      >
                        {t.label}
                      </p>
                      <p
                        className="text-xs sm:text-sm leading-tight mt-0.5"
                        style={{ color: 'rgba(255,255,255,0.72)' }}
                      >
                        {t.sub}
                      </p>
                    </div>
                    {isReferral && (
                      <ChevronRight className="mt-1 h-5 w-5 shrink-0" style={{ color: '#A7EAF2' }} strokeWidth={2.8} />
                    )}
                  </div>
                  {isReferral && (
                    <div
                      className="mt-3 flex items-center gap-2 rounded-2xl px-3 py-2"
                      aria-busy={!referralCodeReady}
                      style={{
                        background: 'rgba(0,0,0,0.26)',
                        border: '1px solid rgba(224,244,247,0.16)',
                      }}
                    >
                      <span
                        className="mono flex-1 truncate text-sm font-black tracking-[0.12em] sm:text-base"
                        style={{ color: '#A7EAF2' }}
                        data-testid="explore-referral-code"
                      >
                        {referralCodeReady ? referralCode : 'Loading referral code…'}
                      </span>
                      <button
                        type="button"
                        onClick={copyReferralCode}
                        disabled={!referralCodeReady}
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl transition active:scale-95 disabled:cursor-wait disabled:opacity-45"
                        style={{
                          background: 'linear-gradient(135deg, #004D61, #006B85)',
                          color: '#E0F4F7',
                          border: '1px solid rgba(224,244,247,0.24)',
                        }}
                        data-testid="explore-referral-code-copy"
                        aria-label={referralCodeReady
                          ? (copiedReferralCode ? 'Referral code copied' : 'Copy referral code')
                          : 'Referral code is loading'}
                      >
                        {copiedReferralCode ? <Check className="h-5 w-5" strokeWidth={3} /> : <Copy className="h-5 w-5" strokeWidth={2.6} />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Logged-in chip */}
        <div className="mt-8 flex justify-center">
          <span
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px]"
            style={{
              background: 'rgba(0,77,97,0.10)',
              border: '1px solid rgba(0,77,97,0.30)',
              color: '#86EFAC',
            }}
          >
            Logged in as {user?.username || user?.email || 'player'}
          </span>
        </div>
      </div>

      <nav
        className="player-bottom-card-nav-shell fixed bottom-0 left-0 right-0 z-[60] w-full px-0 pb-0"
        aria-label="Player quick navigation"
        data-testid="rewards-bottom-card-nav"
      >
        <div
          className="player-bottom-card-nav relative grid grid-cols-5 items-center rounded-t-[22px] rounded-b-none border px-2 py-2 shadow-2xl backdrop-blur-xl"
          style={{
            '--nav-active-index': 0,
            background: 'linear-gradient(135deg, rgba(9,17,27,0.94), rgba(16,24,39,0.9))',
            border: '1.5px solid rgba(255,255,255,0.24)',
            boxShadow: '0 18px 42px rgba(0,0,0,0.56), 0 0 28px rgba(0,77,97,0.14), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="player-bottom-card-nav-item player-bottom-card-nav-item--active"
            data-testid="rewards-nav-rewards"
            aria-current="page"
          >
            <span className="player-bottom-card-nav-icon"><Gift className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Rewards</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(`${homePath}?open=games`)}
            className="player-bottom-card-nav-item"
            data-testid="rewards-nav-games"
          >
            <span className="player-bottom-card-nav-icon"><Gamepad2 className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Games</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(homePath)}
            className="player-bottom-card-nav-item"
            aria-label="Home"
            data-testid="rewards-nav-logo"
          >
            <span className="player-bottom-card-nav-icon"><Home className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Home</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(`${homePath}?open=wallet`)}
            className="player-bottom-card-nav-item"
            data-testid="rewards-nav-wallets"
          >
            <span className="player-bottom-card-nav-icon"><Wallet className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Wallets</span>
          </button>

          <button
            type="button"
            onClick={() => navigate(`${homePath}?open=support`)}
            className="player-bottom-card-nav-item"
            data-testid="rewards-nav-support"
          >
            <span className="player-bottom-card-nav-icon"><Headphones className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Support</span>
          </button>
        </div>
      </nav>

    </div>
  );
}
